import { ITypeMapper, typeMapperHelper } from 'utils/helpers/items';
import { capitalizeString } from 'utils/helpers/string';
import { IOrderItem } from 'utils/interfaces';

// Package Detail Interface
export interface IZplStickerObject {
  orderAlphaId: string;
  fullAddress: string;
  pickupDate: string;
  dropOffDate: string;
  addressBldg: string;
  packageSerialId: string;
  itemsList: IOrderItem[];
}

export interface IFoldedBagLabel {
  orderAlphaId: string;
  customerAlphaId: string;
  geofence: string;
  pickupDate: string;
  dropoffDate: string;
  numberOfFoldingItems: number;
  packageCount: string;
}

// Garment Position mapping
let garmentPositions = new Map();

garmentPositions.set(0, '420');
garmentPositions.set(1, '450');
garmentPositions.set(2, '480');
garmentPositions.set(3, '510');

const zplStickerMap = {
  orderAlphaId: (value: string) => {
    return `\n^FX Order Alpha ID\n^CF0,40\n^FO30,50^FD${value}^FS`;
  },
  addressBldg: (value: string) => {
    return `\n^FX Address Details\n^CF0,20\n ^FO30,90^FD${value}^FS`;
  },
  fullAddress: (value: string) => {
    return `^FO30, 110^FD${value}^FS\n`;
  },
  geofence: (value: string) => {
    return `\n^FX^FX Geofences ID\n^FO30,140^FD${value}^FS`;
  },
  geofenceSequence: (value: string) => {
    return `\n^FX Geofence Sequence\n^FO30,170^FD${value}^FS`;
  },
  pickupDate: (value: string) => {
    return `^FX PU/DO Informations\n^CF0,20\n^FO30, 215^FDPU: ${value}^FS\n`;
  },
  dropOffDate: (value: string) => {
    return `^FO200, 215^FDDO: ${value}^FS\n\n^FX Divider\n^FO30,240^GB700,1,3^FS\n`;
  },
  packageSerialId: (value: string) => {
    return `^FX CODE39 Barcode\n^FO10,50\n^B3N,N,100,Y,N\n^FO50,260^BC^FD${value}^FS\n^FO30,400^GB700,1,3^FS\n`;
  },
};

const bannedPackageItems = (value: IOrderItem[]) => {
  let itemsDisplay = ''

  // eslint-disable-next-line array-callback-return
  value.map((garment, i) => {
    const coord = garmentPositions.get(i);
    itemsDisplay = `${itemsDisplay} \n^FX${garment.code}\n^FO30,${coord}^FD${garment.code}^FS\n^FO230,${coord}^FD${garment.type}^FS\n`;
  });

  return itemsDisplay;
}

const foldingPackageItems = (value: IOrderItem[]) => {
  const itemTypes: ITypeMapper = typeMapperHelper(value);
  let itemsString = ''

  Object.keys(itemTypes).forEach((key: string) => {
    itemsString = `${itemsString}${capitalizeString(key)} (x${itemTypes[key].length}), `
  })

  return `^FX\n
  ^FO30,420^FDItem Count: ^FS\n
  ^FO170,420^FD${value.length}^FS\n

  ^FX
  ^FO30,460^FD${itemsString}^FS`
}

class Parser {
  private _zplBody: string[] = [];

  get zplInstance(): string {
    return ['^XA', '^LH0,0', ...this.zplBody, '^XZ'].join('\n');
  }
  set zplBody(value: string[]) {
    this._zplBody = [...this.zplBody, ...value];
  }
  get zplBody() {
    return this._zplBody;
  }

  /**
   * @param comment
   * @description adds a comment string to the zpl
   */
  addComment(comment: string) {
    this.zplBody = [`^FX ${comment}`];
  }

  addZplLabel(label: string) {
    this.zplBody = [label];
  }

  generateZplSticker(data: IZplStickerObject, isFolding: boolean = false) {
    Object.entries(data).forEach(([key, value]) => {
      if (zplStickerMap[key as 'orderAlphaId']) {
        const result = zplStickerMap[key as 'orderAlphaId'](value);
        return this.zplBody = [result];
      }

      // Slot reserved for itemsList display
      if (key === 'itemsList') {
        const itemsDisplay = isFolding ? foldingPackageItems(value) : bannedPackageItems(value);
        return this.zplBody = [itemsDisplay]
      }
    });
    return this.zplInstance;
  }

  generateZPLBagSticker(bagCode: string) {
    const generateQRCode = `^FO70,100^BY4,2.0,60^BQN,1,10^FDMM,a${bagCode}^FS`;
    const QrCodeDisplay = `^CF0,30^FO100,450^FD${bagCode}^FS`;
    this.zplBody = [generateQRCode, QrCodeDisplay];

    return this.zplInstance;
  }

  generateFoldedBagLabel(foldedBagSticker: IFoldedBagLabel) {
    const { orderAlphaId, customerAlphaId, geofence, pickupDate, dropoffDate, numberOfFoldingItems, packageCount } = foldedBagSticker;

    const orderId = `^CF0,50\n^FO50,50^FDOrder ID: ${orderAlphaId}^FS`;
    const customerId = `^CF0,50\n^FO50,150^FDCX ID:  ${customerAlphaId}^FS`;

    const addressType = `^CF0,20\n^FO50,220^FD${geofence}^FS`;

    const pickup = `^CF0,20\n^FO50,300^FD${pickupDate}^FS`;
    const dropoff = `^CF0,20\n^FO50,350^FD${dropoffDate}^FS`;

    const numberOfFoldingItem = `^CF0,20\n^FO50,400^FD# of folding items: ${numberOfFoldingItems}^FS`;
    const packagingCount = `^CF0,20\n^FO50,500^FDPackage: ${packageCount}^FS`;

    this.zplBody = [orderId, customerId, addressType, pickup, dropoff, numberOfFoldingItem, packagingCount]
    return this.zplInstance
  }

  clearZplInstance = () => {
    this._zplBody = [];
  };
}

const parser = new Parser();

export default parser;
